// Crew barks and the 1MC. No synthesized speech — the text does the talking
// (a chip floating over the sailor's head) and the audio does the texture: a
// short sound-powered-phone murmur of filtered blips that *suggests* a voice
// without attempting one. Lines are context-aware: where the sailor is
// standing, what state the ship is in, and what the fire-control problem
// needs from you next. The 1MC runs the ship's day over the announcing
// system between actions, and chow call actually pulls the crew to the mess.

import * as THREE from "three";

const LINES = {
  deck: [
    "Word is we're going stateside after this one.",
    "She rides smooth for 57,000 tons, don't she, sir?",
    "Mind the knee-knockers, sir.",
    "Lookouts get the sunsets. Snipes get the steam. Everybody's happy.",
    "Painted that bulkhead this morning, sir. Try not to lean on her.",
  ],
  mess: [
    "Sliders and steamed rice again, sir.",
    "The milk machine's the real main battery, ask anybody.",
    "Take a tray, sir — the line moves fast.",
    "Cooks heard the salvo buzzer and never dropped a ladle.",
  ],
  gedunk: [
    "Strawberry's out. Chocolate's the smart money anyway, sir.",
    "Nickel a scoop, sir, and worth every round we carry.",
  ],
  machinery: [
    "She'll answer any bell you ring, sir.",
    "Four boilers on the line, superheat's up.",
    "Watch the deck plates, sir — she's oily aft.",
    "You want speed, we've got speed. You want quiet, wrong deck.",
  ],
  plot: [
    "Rangekeeper's warm, sir. Feed us rates and we'll feed you a solution.",
    "Generated's drifting off the dots — somebody's course guess is bad.",
    "Only one course and speed makes both rates agree, sir. Find it.",
  ],
  magazine: [
    "Powder's dry and the sprinklers are wet, sir. Best we can ask.",
    "Six hundred sixty pounds a charge, sir. Mind your cigarette. Kidding. MIND IT.",
  ],
  gq: [
    "All stations manned and ready, sir!",
    "Talker says the director's on. Any minute now.",
    "First salvo's the loudest, sir. They're all the loudest.",
    "Condition ZED set. She's buttoned up tight.",
  ],
  needRates: [
    "Plot's begging for rates, sir — the rangekeeper's on the third deck. T gets you there.",
    "Solution won't converge itself anymore, sir. Somebody's got to crank it.",
  ],
  fire: [
    "Repair party's on it, sir — stay clear of the smoke.",
    "Hold E on the flames if you're feeling useful, sir.",
  ],
  night: [
    "Darken ship, sir. Not so much as a cigarette topside.",
    "They say you hear the Long Lance coming. You don't. You see the wake, or nothing.",
    "Star shell doctrine, sir: burst BEYOND them, silhouette the hull.",
  ],
  korea: [
    "Kalmagak's been quiet all watch, sir. That's how they get you.",
    "Third war for the old girl. She was mothballs eighteen months ago.",
  ],
  vietnam: [
    "Spotter bird's up over the beach, sir.",
    "They shoot back on this gunline, sir. Cap Mui Lay taught us that.",
  ],
  lebanon: [
    "Mind the city under the gun-target line, sir.",
    "Sixteen-inch over a city. Every solution better be clean, sir.",
  ],
};

const ANNOUNCEMENTS = [
  "SWEEPERS, SWEEPERS, MAN YOUR BROOMS. GIVE THE SHIP A CLEAN SWEEP-DOWN FORE AND AFT.",
  "NOW HEAR THIS — THE SMOKING LAMP IS LIT IN ALL AUTHORIZED SPACES.",
  "CHOW CALL, CHOW CALL — DINNER FOR THE CREW.", // pulls the crew to the mess
  "NOW MUSTER THE ANCHOR DETAIL... BELAY MY LAST.",
  "NOW HEAR THIS — MAIL CALL, MAIL CALL. THE FANTAIL, ON THE DOUBLE.",
  "THE GEDUNK STAND IS OPEN. THE GEDUNK STAND IS OPEN.",
  "NOW RELIEVE THE WATCH. ON DECK, SECTION THREE.",
];

export class Barks {
  constructor(G) {
    this.G = G;
    this.el = document.createElement("div");
    this.el.id = "bb-bark";
    this.el.className = "hidden";
    document.body.appendChild(this.el);
    this.active = null;      // {sailor, ttl}
    this.checkAcc = 0;
    this.globalCool = 6;     // first bark comes easy
    this.mcTimer = 75;       // first 1MC announcement
    this.mcIdx = (Math.random() * ANNOUNCEMENTS.length) | 0;
    this._v = new THREE.Vector3();
  }

  contextLines(sailor) {
    const G = this.G;
    const room = G.roomAt(sailor.pos);
    const pool = [];
    // the most useful hint wins a heavy weight: rates wanted at the plot
    if (G.fc.target && G.fc.quality < 0.45 && !G.fc.plotManned) pool.push(...LINES.needRates, ...LINES.needRates);
    if (G.damage.comps.some((c) => c.fire > 0.05)) pool.push(...LINES.fire, ...LINES.fire);
    if (G.gq) pool.push(...LINES.gq, ...LINES.gq);
    if (G.night) pool.push(...LINES.night, ...LINES.night);
    if (G.mission && LINES[G.mission.id]) pool.push(...LINES[G.mission.id]);
    const key = room?.key ?? "";
    if (key === "mess") pool.push(...LINES.mess);
    else if (key === "gedunk") pool.push(...LINES.gedunk);
    else if (key === "engine" || key === "fireroom" || key === "steering") pool.push(...LINES.machinery);
    else if (key === "plot" || key === "gyro") pool.push(...LINES.plot);
    else if (key === "magazine" || key === "projflat") pool.push(...LINES.magazine);
    if (!pool.length || Math.random() < 0.35) pool.push(...LINES.deck);
    return pool;
  }

  update(dt) {
    const G = this.G;
    this.globalCool -= dt;
    this.runMC(dt);

    // keep the active chip pinned over its sailor
    if (this.active) {
      this.active.ttl -= dt;
      const s = this.active.sailor;
      const gone = s.pos.distanceTo(G.player.pos) > 9;
      if (this.active.ttl <= 0 || gone || G.stations.active || G.hud.tpOpen || G.shellCam?.on || G.over) {
        this.active = null;
        this.el.classList.add("hidden");
      } else {
        const w = this._v.set(s.pos.x, s.pos.y + 2.05, s.pos.z);
        G.shipInner.localToWorld(w);
        const behind = w.clone().applyMatrix4(G.camera.matrixWorldInverse).z > -0.1;
        w.project(G.camera);
        if (behind || Math.abs(w.x) > 1.05 || Math.abs(w.y) > 1.1) this.el.classList.add("hidden");
        else {
          this.el.classList.remove("hidden");
          this.el.style.left = `${(w.x * 0.5 + 0.5) * window.innerWidth}px`;
          this.el.style.top = `${(-w.y * 0.5 + 0.5) * window.innerHeight}px`;
        }
      }
      return;
    }

    // look for someone to talk to a couple of times a second
    this.checkAcc += dt;
    if (this.checkAcc < 0.5 || this.globalCool > 0) return;
    this.checkAcc = 0;
    if (G.stations.active || G.hud.tpOpen || G.shellCam?.on || G.over) return;
    const p = G.player.pos;
    let best = null, bd = 4.6;
    for (const s of G.crew.sailors) {
      if (s.working) continue;
      if ((s.barkCool ?? 0) > performance.now()) continue;
      const d = Math.hypot(s.pos.x - p.x, s.pos.z - p.z) + Math.abs(s.pos.y - p.y);
      if (d < bd) { bd = d; best = s; }
    }
    if (!best) return;
    const pool = this.contextLines(best);
    const text = pool[(Math.random() * pool.length) | 0];
    best.barkCool = performance.now() + 60000;
    this.globalCool = 11 + Math.random() * 14;
    this.active = { sailor: best, ttl: 4.6 };
    this.el.textContent = text;
    this.el.classList.remove("hidden");
    G.audio.murmur();
  }

  // The ship's day, over the announcing system. Quiet at GQ — the 1MC has
  // other things to say then, and the klaxon says them.
  runMC(dt) {
    const G = this.G;
    if (G.gq || G.over) return;
    this.mcTimer -= dt;
    if (this.mcTimer > 0) return;
    this.mcTimer = 120 + Math.random() * 80;
    const text = ANNOUNCEMENTS[this.mcIdx % ANNOUNCEMENTS.length];
    this.mcIdx++;
    G.audio.bosunPipe();
    G.hud.log(`1MC: ${text}`);
    if (text.startsWith("CHOW CALL")) G.crew.mealtime = 90;
  }
}
